import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Plug, RotateCcw, Trash2 } from 'lucide-react';
import {
  ApiError,
  createWebhook,
  deleteWebhook,
  getWebhookDeliveries,
  getWebhooks,
  qk,
  retryWebhookDelivery,
  WEBHOOK_EVENT_CATALOG,
  type Webhook,
  type WebhookDelivery,
} from '../api.js';
import BilgiDugmesi from '../components/BilgiDugmesi.js';
import Badge, { type BadgeTone } from '../components/Badge.js';
import Card from '../components/Card.js';
import EmptyState, { ErrorState } from '../components/EmptyState.js';
import { LoadingBlock } from '../components/Spinner.js';
import { useOrgContext } from '../components/Layout.js';
import { orgRoleAtLeast } from '../lib/auth.js';
import { formatDateTime } from '../lib/format.js';

/**
 * §47.3 — Webhook'lar sayfası: uç kaydı · son teslimatlar · `dead` için "yeniden dene".
 *
 * SAYFA ORG DÜZEYİNDEDİR (proje değil): `webhooks.org_id` şemanın kendisidir (§47.2)
 * ve olaylar org'un tüm projelerinden akar. Bu yüzden aktif projeyi değil
 * `useOrgContext()`i okur — Faturalama sayfasıyla aynı karar.
 *
 * YOL İNGİLİZCEDİR (`/webhooks`): §47 boyunca terimin kendisi budur ve müşteri
 * dokümantasyonu da öyle anar — `/heatmap` ve `/seo` ile aynı gerekçe (§23.5/§24.7).
 *
 * BU SAYFANIN TUTTUĞU İKİ KURAL:
 *  1. **Sır BİR KEZ gösterilir** (§47.2). Yaratma yanıtındaki `secret` bir kutuda
 *     çıkar ve sayfa yenilenince KAYBOLUR; liste ucu onu zaten taşımaz. Kalıcı olarak
 *     ekranda duran bir sır, ekran paylaşımıyla sızar.
 *  2. **"Yeniden dene" YALNIZ `dead` satırda görünür.** Uç 409 döndüğü için bu bir
 *     güvenlik değil nezaket kontrolüdür; `delivered` bir satırı canlandırmak ÇİFT
 *     TESLİMAT olurdu ve düğmeyi çizmek onu meşru bir seçenek gibi gösterirdi.
 */

/** §47 panel yolu — Layout rayı ve App rotası buna bağlanır. */
export const WEBHOOKS_PATH = '/webhooks';

/** §47.2 teslimat durumu → rozet. Renk TEK BAŞINA taşıyıcı değildir; metin de söyler. */
const DURUM: Record<WebhookDelivery['status'], { label: string; tone: BadgeTone; title: string }> = {
  pending: {
    label: 'kuyrukta',
    tone: 'neutral',
    title: 'Randevusunu bekliyor; worker turu geldiğinde denenecek.',
  },
  delivered: {
    label: 'teslim edildi',
    tone: 'success',
    title: 'Alıcı 2xx döndü (3xx başarı sayılmaz).',
  },
  dead: {
    label: 'ölü',
    tone: 'danger',
    title: '5 deneme tükendi. "Yeniden dene" AYNI satırı canlandırır (§47.3).',
  },
};

/**
 * §47.2 — `last_status = 0` bir HTTP kodu DEĞİLDİR: ağ hatası/zaman aşımı demektir.
 * Ham `0` yazmak operatörü "sunucu 0 döndü" diye yanlış yere bakmaya iterdi.
 */
function sonYanit(delivery: WebhookDelivery): string {
  if (delivery.lastStatus === null) return '—';
  if (delivery.lastStatus === 0) return 'ağ hatası';
  return String(delivery.lastStatus);
}

/* -------------------------------------------------------------------------- */
/* Teslimat listesi                                                            */
/* -------------------------------------------------------------------------- */

function Teslimatlar({ oid, webhook, yetkili }: { oid: string; webhook: Webhook; yetkili: boolean }) {
  const client = useQueryClient();
  const deliveries = useQuery({
    queryKey: qk.webhookDeliveries(oid, webhook.id),
    queryFn: ({ signal }) => getWebhookDeliveries(oid, webhook.id, signal),
    // Teslimat kuyruğu 10 sn'de bir dönüyor; panelin canlı görünmesi için makul aralık.
    refetchInterval: 15_000,
  });

  const yenidenDene = useMutation({
    mutationFn: (deliveryId: number) => retryWebhookDelivery(oid, webhook.id, deliveryId),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.webhookDeliveries(oid, webhook.id) });
    },
  });

  if (deliveries.isLoading) return <LoadingBlock />;
  if (deliveries.isError) {
    return <ErrorState title="Teslimatlar okunamadı" description={String(deliveries.error)} />;
  }

  const veri = deliveries.data;
  if (veri === undefined) return null;

  if (veri.deliveries.length === 0) {
    return (
      <p className="text-[13px] text-[#6b7280]">
        Henüz teslimat yok — bu uca abone olduğunuz bir olay gerçekleşmedi.
      </p>
    );
  }

  return (
    <div className="space-y-3">
      {veri.dead > 0 ? (
        <p className="rounded-md bg-[#fef2f2] px-3 py-2 text-[13px] text-red-700">
          <strong>{veri.dead} teslimat ölü.</strong> Alıcı adresini doğrulayın, sonra “yeniden
          dene” ile aynı satırı canlandırın — yeni satır açılmaz (§47.3).
        </p>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="text-left text-[12px] text-[#9ca3af]">
              <th className="py-1 pr-4">Olay</th>
              <th className="py-1 pr-4">Durum</th>
              <th className="py-1 pr-4">Deneme</th>
              <th className="py-1 pr-4">Son yanıt</th>
              <th className="py-1 pr-4">Oluşturuldu</th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {veri.deliveries.map((d) => {
              const durum = DURUM[d.status];
              return (
                <tr key={d.id} className="border-t border-[#f3f4f6]" data-testid="teslimat-satiri">
                  <td className="py-1.5 pr-4 font-mono text-[12px] text-[#374151]">{d.event}</td>
                  <td className="py-1.5 pr-4">
                    <Badge tone={durum.tone} size="sm" title={durum.title}>
                      {durum.label}
                    </Badge>
                  </td>
                  <td className="num py-1.5 pr-4">{d.attempts}</td>
                  <td className="py-1.5 pr-4 text-[#6b7280]">{sonYanit(d)}</td>
                  <td className="py-1.5 pr-4 text-[#6b7280]">{formatDateTime(d.createdAt)}</td>
                  <td className="py-1.5 text-right">
                    {/* §47.3 — düğme YALNIZ `dead` satırda; diğerlerinde uç 409 döner. */}
                    {d.status === 'dead' && yetkili ? (
                      <button
                        type="button"
                        data-testid="teslimat-yeniden-dene"
                        onClick={() => yenidenDene.mutate(d.id)}
                        disabled={yenidenDene.isPending}
                        className="inline-flex items-center gap-1 rounded-lg border border-[#e5e7eb] px-2 py-1 text-[12px] font-medium text-[#374151] disabled:opacity-50"
                      >
                        <RotateCcw size={12} /> Yeniden dene
                      </button>
                    ) : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {veri.truncated ? (
        /* §33.5 — kesilmiş liste SESSİZ kalmaz; kullanıcı eksik veriye tam veri sanmamalı. */
        <p className="text-[12px] text-[#9ca3af]">
          Yalnız son {veri.deliveries.length} teslimat gösteriliyor.
        </p>
      ) : null}

      {yenidenDene.isError ? <ErrorState description={String(yenidenDene.error)} /> : null}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Uç kartı                                                                    */
/* -------------------------------------------------------------------------- */

function UcKarti({ oid, webhook, yetkili }: { oid: string; webhook: Webhook; yetkili: boolean }) {
  const client = useQueryClient();
  const [acik, setAcik] = useState(false);

  const sil = useMutation({
    mutationFn: () => deleteWebhook(oid, webhook.id),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.webhooks(oid) });
    },
  });

  return (
    <div data-testid="webhook-satiri">
    <Card
      title={<span className="break-all font-mono text-[13px]">{webhook.url}</span>}
      subtitle={
        <span className="flex flex-wrap gap-1.5">
          {webhook.events.map((e) => (
            <Badge key={e} tone="petrol" size="sm">
              {e}
            </Badge>
          ))}
        </span>
      }
      actions={
        <div className="flex items-center gap-2">
          <Badge tone={webhook.active ? 'success' : 'outline'} size="sm">
            {webhook.active ? 'aktif' : 'pasif'}
          </Badge>
          <button
            type="button"
            data-testid="webhook-teslimatlar"
            onClick={() => setAcik((v) => !v)}
            className="rounded-lg border border-[#e5e7eb] px-2.5 py-1 text-[12px] font-medium text-[#374151]"
          >
            {acik ? 'Teslimatları gizle' : 'Teslimatlar'}
          </button>
          {yetkili ? (
            <button
              type="button"
              data-testid="webhook-sil"
              onClick={() => sil.mutate()}
              disabled={sil.isPending}
              className="inline-flex items-center gap-1 rounded-lg border border-[#e5e7eb] px-2.5 py-1 text-[12px] font-medium text-red-700 disabled:opacity-50"
              title="Uç silinince teslimat geçmişi de kapanır (§47.2 CASCADE)."
            >
              <Trash2 size={12} /> Sil
            </button>
          ) : null}
        </div>
      }
    >
      {sil.isError ? <ErrorState description={String(sil.error)} /> : null}
      {acik ? <Teslimatlar oid={oid} webhook={webhook} yetkili={yetkili} /> : null}
    </Card>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Kayıt formu                                                                 */
/* -------------------------------------------------------------------------- */

function YeniUcFormu({ oid, onDone }: { oid: string; onDone: () => void }) {
  const client = useQueryClient();
  const [url, setUrl] = useState('');
  const [events, setEvents] = useState<string[]>(['insight.created']);
  /** §47.2 — sır YALNIZ burada, YALNIZ bir kez. State'te tutulur, hiçbir yere yazılmaz. */
  const [secret, setSecret] = useState<string | null>(null);

  const olustur = useMutation({
    mutationFn: () => createWebhook(oid, { url: url.trim(), events }),
    onSuccess: (res) => {
      setSecret(res.secret);
      setUrl('');
      void client.invalidateQueries({ queryKey: qk.webhooks(oid) });
    },
  });

  if (secret !== null) {
    return (
      <Card title="Uç kaydedildi — imza sırrınız">
        <p className="text-[13px] text-[#6b7280]">
          Bu sır <strong>bir daha gösterilmeyecek</strong> (§47.2). Şimdi kopyalayın; kaybolursa
          ucu silip yeniden kaydetmeniz gerekir.
        </p>
        <code
          data-testid="webhook-sir"
          className="mt-3 block break-all rounded-md bg-[#f3f4f6] px-3 py-2 font-mono text-[12px] text-[#111827]"
        >
          {secret}
        </code>
        <p className="mt-3 text-[12px] text-[#9ca3af]">
          Alıcı tarafta <code>x-dxp-signature</code> başlığını HAM gövde üzerinde doğrulayın;
          örnek kod RUNBOOK §47’dedir.
        </p>
        <button
          type="button"
          data-testid="webhook-sir-kapat"
          onClick={() => {
            setSecret(null);
            onDone();
          }}
          className="mt-4 rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white"
        >
          Kopyaladım, kapat
        </button>
      </Card>
    );
  }

  return (
    <Card title="Yeni uç">
      <form
        data-testid="webhook-formu"
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (url.trim() !== '' && events.length > 0) olustur.mutate();
        }}
      >
        <div>
          <label className="text-[12px] text-[#6b7280]" htmlFor="webhook-url">
            Alıcı adresi (HTTPS)
          </label>
          <input
            id="webhook-url"
            data-testid="webhook-url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="mt-1 w-full rounded-lg border border-[#e5e7eb] px-3 py-2 font-mono text-[13px]"
            placeholder="https://ornek.com/dxp-kanca"
          />
          {/* §47.4 — özel/loopback adres KAYIT ANINDA reddedilir; kural önden söylenir. */}
          <p className="mt-1 text-[12px] text-[#9ca3af]">
            Özel ağ adresleri (10.x, 192.168.x, localhost) kabul edilmez.
          </p>
        </div>

        <fieldset>
          <legend className="text-[12px] text-[#6b7280]">Olaylar</legend>
          <div className="mt-1 flex flex-wrap gap-3">
            {WEBHOOK_EVENT_CATALOG.map((olay) => (
              <label key={olay} className="flex items-center gap-1.5 text-[13px] text-[#374151]">
                <input
                  type="checkbox"
                  checked={events.includes(olay)}
                  onChange={(e) =>
                    setEvents((önceki) =>
                      e.target.checked ? [...önceki, olay] : önceki.filter((x) => x !== olay),
                    )
                  }
                />
                <span className="font-mono text-[12px]">{olay}</span>
              </label>
            ))}
          </div>
        </fieldset>

        {olustur.isError ? (
          <ErrorState
            title="Uç kaydedilemedi"
            description={
              olustur.error instanceof ApiError && olustur.error.status === 403
                ? 'Planınız webhook özelliğini içermiyor (§47.5/4). Faturalama sayfasından yükseltebilirsiniz.'
                : String(olustur.error)
            }
          />
        ) : null}

        <button
          type="submit"
          data-testid="webhook-olustur"
          disabled={olustur.isPending || url.trim() === '' || events.length === 0}
          className="rounded-lg bg-brand px-4 py-2 text-[13px] font-medium text-white disabled:opacity-50"
        >
          Ucu kaydet
        </button>
      </form>
    </Card>
  );
}

/* -------------------------------------------------------------------------- */

export default function Webhooks() {
  const { orgId, orgRole } = useOrgContext();
  const [formAcik, setFormAcik] = useState(false);

  /*
   * §47 RBAC — kayıt/silme/retry org:admin ister. Kontrol GÜVENLİK DEĞİL (sunucu
   * kararı tekrar verir), kesin 403 alacak düğmeyi tıklatmama nezaketidir; liste
   * `member`a AÇIKTIR ve entegrasyonun durumunu herkes görebilir.
   */
  const yetkili = orgRoleAtLeast(orgRole, 'admin');

  const webhooks = useQuery({
    queryKey: qk.webhooks(orgId ?? ''),
    queryFn: ({ signal }) => getWebhooks(orgId as string, signal),
    enabled: orgId !== null,
  });

  return (
    <div className="min-h-full bg-[#fafafa]" data-testid="webhooks-sayfasi">
      <div className="border-b border-[#e5e7eb] bg-white px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-[18px] font-semibold text-[#111827]">Webhook’lar</h1>
              <BilgiDugmesi testId="webhooks-bilgi">Paneli açmadan haberdar olmak için: seçtiğiniz olaylar sizin verdiğiniz adrese imzalı POST olarak gönderilir. Başarısız teslimatlar geri çekilme merdiveniyle yeniden denenir; teslimat geçmişini buradan izlersiniz.</BilgiDugmesi>
            </div>
            <p className="text-[13px] text-[#6b7280]">
              Olaylar imzalı olarak sizin sisteminize gönderilir; başarısız teslimatlar geri
              çekilme merdiveniyle yeniden denenir.
            </p>
          </div>
          {yetkili ? (
            <button
              type="button"
              data-testid="webhook-yeni"
              onClick={() => setFormAcik((v) => !v)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-[#e5e7eb] px-3 py-1.5 text-[13px] font-medium text-[#374151]"
            >
              <Plug size={14} /> Yeni uç
            </button>
          ) : null}
        </div>
      </div>

      <div className="space-y-6 px-6 py-8">
        {orgId === null ? (
          <EmptyState
            title="Organizasyon seçilmedi"
            description="Webhook’lar organizasyona asılıdır (§47.2). Sol üstten bir organizasyon seçin."
          />
        ) : (
          <>
            {formAcik ? <YeniUcFormu oid={orgId} onDone={() => setFormAcik(false)} /> : null}

            {webhooks.isLoading ? (
              <LoadingBlock />
            ) : webhooks.isError ? (
              <ErrorState title="Uçlar okunamadı" description={String(webhooks.error)} />
            ) : (webhooks.data?.webhooks ?? []).length === 0 ? (
              <EmptyState
                title="Henüz webhook yok"
                description="Bir uç kaydedin; yeni bulgu, tarama sonucu, kota eşiği ve abonelik değişimleri imzalı POST olarak sisteminize düşsün."
              />
            ) : (
              (webhooks.data?.webhooks ?? []).map((w) => (
                <UcKarti key={w.id} oid={orgId} webhook={w} yetkili={yetkili} />
              ))
            )}
          </>
        )}
      </div>
    </div>
  );
}
